import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { api, ApiError, SecretStatus } from "../../lib/api.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

function kindLabel(kind: SecretStatus["kind"]): string {
  switch (kind) {
    case "dhis2-pat":         return "DHIS2 personal access token";
    case "dhis2-basic":       return "DHIS2 basic credentials";
    case "webhook-signing":   return "Webhook signing key";
    case "forwarding-token":  return "Forwarding bearer token";
    case "portal-access-key": return "Public-portal access key";
    default:                  return String(kind);
  }
}

function statusPill(s: SecretStatus["status"]) {
  switch (s) {
    case "configured": return <span className="pill pill-ok">Configured</span>;
    case "missing":    return <span className="pill pill-err">Missing</span>;
    case "rotating":   return <span className="pill pill-warn">Rotating</span>;
    default:           return <span className="pill pill-muted">{s}</span>;
  }
}

function storagePill(storage: SecretStatus["storage"]) {
  if (storage === "server-side-vault") return <span className="pill pill-info">Server vault</span>;
  if (storage === "dhis2-route")       return <span className="pill pill-info">DHIS2 route</span>;
  return <span className="pill pill-muted">{storage}</span>;
}

function fmt(date?: string): string {
  return date ? new Date(date).toLocaleString() : "—";
}

export default function AdminSecrets() {
  const [secrets, setSecrets] = useState<SecretStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const { secrets } = await api.security();
      setSecrets([...(secrets ?? [])].sort((a, b) => a.label.localeCompare(b.label)));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const kinds = useMemo(
    () => Array.from(new Set(secrets.map((s) => s.kind))).sort(),
    [secrets]
  );

  const filtered = useMemo(() => {
    return secrets.filter((s) => {
      if (kindFilter !== "all" && s.kind !== kindFilter) return false;
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      return true;
    });
  }, [secrets, kindFilter, statusFilter]);

  const tSecrets = useTableState(filtered as unknown as Array<Record<string, unknown>>, {
    searchFields: ["id", "label", "kind", "status", "storage", "exposure"],
    initialPageSize: 25
  });

  const total = secrets.length;
  const configured = secrets.filter((s) => s.status === "configured").length;
  const missing = secrets.filter((s) => s.status === "missing").length;
  const rotating = secrets.filter((s) => s.status === "rotating").length;

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Secrets inventory</h1>
          <p>All secrets the platform stewards on behalf of workspaces. Values are server-only and never returned to the UI.</p>
        </div>
        <div className="actions">
          <button className="btn" onClick={() => void refresh()} disabled={loading}>Refresh</button>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      <section className="section">
        <div className="stat-grid">
          <div className="stat"><div className="stat-label">Secrets</div><div className="stat-value">{total}</div></div>
          <div className="stat"><div className="stat-label">Configured</div><div className="stat-value">{configured}</div></div>
          <div className="stat"><div className="stat-label">Missing</div><div className="stat-value">{missing}</div></div>
          <div className="stat"><div className="stat-label">Rotating</div><div className="stat-value">{rotating}</div></div>
        </div>
      </section>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <label className="muted" style={{ fontSize: 12 }}>Kind</label>
        <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
          <option value="all">All ({kinds.length})</option>
          {kinds.map((k) => <option key={k} value={k}>{kindLabel(k)}</option>)}
        </select>
        <label className="muted" style={{ fontSize: 12, marginLeft: 12 }}>Status</label>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="configured">Configured</option>
          <option value="missing">Missing</option>
          <option value="rotating">Rotating</option>
        </select>
      </div>

      {loading && secrets.length === 0 ? (
        <SkeletonTable rows={5} columns={4} />
      ) : filtered.length === 0 ? (
        <div className="empty">
          <p>No secrets match the current filters.</p>
        </div>
      ) : (
        <div>
          <TableToolbar
            search={tSecrets.search} setSearch={tSecrets.setSearch}
            page={tSecrets.page} setPage={tSecrets.setPage}
            pageSize={tSecrets.pageSize} setPageSize={tSecrets.setPageSize}
            totalPages={tSecrets.totalPages} totalRows={tSecrets.totalRows}
            hideExport
            searchPlaceholder="Search label / kind / status…"
          />
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Kind</th>
                <th>Storage</th>
                <th>Status</th>
                <th>Exposure</th>
                <th>Last validated</th>
              </tr>
            </thead>
            <tbody>
              {(tSecrets.paged as unknown as SecretStatus[]).map((s) => (
                <tr key={s.id}>
                  <td><strong>{s.label}</strong><div className="soft" style={{ fontSize: 12 }}><code>{s.id}</code></div></td>
                  <td className="soft">{kindLabel(s.kind)}</td>
                  <td>{storagePill(s.storage)}</td>
                  <td>{statusPill(s.status)}</td>
                  <td className="soft">{s.exposure}</td>
                  <td className="soft">{fmt(s.lastValidatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
